import { Component } from '@angular/core';
import { AuthService } from './auth/service/auth.service';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeapService } from './api-client/heap.service';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { ClipboardService } from 'ngx-clipboard';
import { NotificationService } from './shared/notification-service/notification.service';
import { LogLevel } from './logger.service';
import { Log } from './logger.service';
import { AppRoutingModule } from './app-routing.module';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BillingService } from './core/services/billing.service';
import Hotjar from '@hotjar/browser';
import * as Sentry from '@sentry/angular';
import { globalOptions } from './shared/highcharts-themes';
import * as Highcharts from 'highcharts';
import { DarkModeService } from './shared/dark-mode/dark-mode.service';

const siteId = 5182445;
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);

@Component({
    selector: 'app-root',
    templateUrl: './bootstrap.component.html',
    styleUrls: ['./bootstrap.component.scss'],
    standalone: false
})
export class BootstrapComponent {
  $isLoggedIn: Observable<boolean>;
  appExperience: 'workforce' | 'gotlivedata' = 'workforce';
  appVersion: string;
  private intercomOnHideRegistered = false;

  constructor(
    private auth: AuthService,
    private heapService: HeapService,
    private _hotkeysService: HotkeysService,
    private _clipboardService: ClipboardService,
    private notify: NotificationService,
    private appRoutingModule: AppRoutingModule,
    private router: Router,
    private darkModeService: DarkModeService,
    private billingService: BillingService
  ) {
    this.heapService.load({
      app_id: environment.heapAppId,
      force_ssl: true,
      secure_cookie: true,
      disable_text_capture: false,
      cookie_path: '/',
    });
    this.appVersion = environment.appVersion; // TODO - do we need this?
  }

  ngOnInit() {
    this.$isLoggedIn = this.auth.$isLoggedIn;
    // Workforce is the default experience everywhere; GLD only via Alt+Shift+W.
    this.appExperience = 'workforce';
    Highcharts.setOptions(globalOptions); // we set it here globally for all children b/c it's CSS-driven (using CSS variables)

    // Stripe checkout redirects back to a landing route (e.g. /chat) with
    // `?stripeResults=...`. Handle it globally here so the new plan/quota are
    // picked up no matter where Stripe lands the user (BillingService guards
    // against running twice for the same return).
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const queryParams = this.router.parseUrl(this.router.url).queryParams;
        this.billingService.handleStripeReturn(queryParams);
      });

    this.auth.$isAdmin.subscribe((isAdmin) => {
      if (!isAdmin) return;

      this.registerGlobalHotkeys();
    });

    // In dev mode we want to allow hotkeys for any user
    if (!environment.production) {
      this.registerGlobalHotkeys();
    }

    this.$isLoggedIn.subscribe((isLoggedIn) => {
      if (!isLoggedIn) return;

      const is_employee = this.auth.email
        ? this.auth.email.endsWith('livedatatechnologies.com')
        : false;

      (window as any)['Intercom']('update', {
        app_id: environment.intercomAppId,
        email: this.auth.email,
        user_id: this.auth.getUserValue.id,
        last_request_at: new Date().getTime() / 1000,
        alignment: 'left',
        horizontal_padding: 170,
        is_employee: is_employee,
        platform_mode: this.appExperience,
        theme_mode: this.darkModeService.getThemeModeValue(),
        skip_fin: false,
      });

      // Reset skip_fin after the messenger closes so Fin re-engages on the next direct
      // launcher click. AI-directive handoffs set skip_fin=true right before opening.
      // Register onHide once — Intercom appends callbacks rather than replacing them.
      if (!this.intercomOnHideRegistered) {
        (window as any)['Intercom']('onHide', () => {
          (window as any)['Intercom']('update', { skip_fin: false });
        });
        this.intercomOnHideRegistered = true;
      }
      Sentry.setUser({ email: this.auth.getUserValue.email, id: this.auth.getUserValue.id });
      this.heapService.identify(this.auth.email || '');
      this.heapService.addUserProperties({
        is_employee: is_employee,
      });
      Hotjar.identify(this.auth.getUserValue.id, {
        is_employee: is_employee,
        created_at: this.auth.getUserValue.createdAt,
        email: this.auth.getUserValue.email,
        source: this.auth.getUserValue.source || 'Unknown',
      });
    });
  }

  triggerSessionRefresh() {
    this.auth.refreshSession().subscribe({
      next: () => {
        this._clipboardService.copyFromContent(this.auth.getAccessTokenValue);
        this.notify.success('Refreshed and copied to clipboard');
      },
      error: () => {
        this.notify.error('Error');
      },
    });
  }

  private registerGlobalHotkeys() {
    this._hotkeysService.reset();
    this._hotkeysService.add([
      new Hotkey(
        'alt+shift+r',
        (event: KeyboardEvent): boolean => {
          this.triggerSessionRefresh();
          return true;
        },
        undefined,
        'Refresh Session & Copy Token'
      ),
      new Hotkey(
        'alt+shift+a',
        (event: KeyboardEvent): boolean => {
          this.auth.toggleAdminVisibility();
          return true;
        },
        undefined,
        'Show/Hide Admin Items'
      ),
      new Hotkey(
        'alt+shift+d',
        (event: KeyboardEvent): boolean => {
          Log.setLogLevel(LogLevel.Debug);
          return true;
        },
        undefined,
        'Set Log Level to Debug'
      ),
      new Hotkey(
        'alt+shift+w',
        (event: KeyboardEvent): boolean => {
          if (this.appExperience === 'workforce') {
            this.appExperience = 'gotlivedata';
          } else {
            this.appExperience = 'workforce';
          }
          this.appRoutingModule.configureRoutes(this.appExperience);
          this.router.navigateByUrl('/');

          return true;
        },
        undefined,
        'Toggle Workforce/GotLivedata'
      ),
    ]);
  }
}
